import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';

import { getNonOrganizerParticipants } from '../utils/export-types';
import { Avatar } from '../utils/participant-utils';

import type { Meeting } from '../utils/export-types';

const MAX_STACK = 5;

export const ParticipantsPanel: FC<{ meeting: Meeting }> = ({ meeting }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const participants = useMemo(
    () => getNonOrganizerParticipants(meeting.participants),
    [meeting.participants],
  );

  const stackVisible = useMemo(() => participants.slice(0, MAX_STACK), [participants]);
  const remaining = participants.length - stackVisible.length;

  return (
    <div className="border-t border-gray-100 pt-4 mt-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
        {t('meeting-secretary.meetings.participants')} ({participants.length})
      </p>

      {participants.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {stackVisible.map((p, i) => (
              <Avatar key={p.id} name={p.name} email={p.email} className={i > 0 ? '-ml-2' : ''} />
            ))}
            {remaining > 0 && (
              <span className="w-9 h-9 text-xs font-medium bg-gray-100 text-gray-500 rounded-full flex items-center justify-center ring-2 ring-white -ml-2">
                +{remaining}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setIsOpen((open) => !open)}
            className="text-sm text-brand flex items-center gap-0.5 focus:outline-none"
          >
            {t('meeting-secretary.meetings.view-all-participants')}
            {isOpen ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
          </button>
        </div>
      )}

      {isOpen && (
        <div className="mt-3 space-y-0.5">
          {participants.map((p) => (
            <div key={p.id} className="flex items-center gap-3 px-2 py-1.5 rounded-lg">
              <Avatar name={p.name} email={p.email} />
              <div className="min-w-0 flex-1">
                {p.name && (
                  <p className="text-sm font-medium text-gray-900 truncate" title={p.name}>
                    {p.name}
                  </p>
                )}
                <p className="text-sm text-gray-500 truncate" title={p.email}>
                  {p.email}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
